import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  createReportRequest,
  getHoldings,
} from "../../../api/inform";
import { getActiveClientsApi } from "../../../api/company";
import { getDeliveryUsers } from "../../../api/user";
import SearchDropdown from "../../../components/searchDropdown";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../../components/dateRangeFilter/DateRangeFilter";
import ReportRequestList, { humanizeReportType } from "../../../components/reportRequestList/ReportRequestList";
import useAuth from "../../../hooks/useAuth";
import useReportRequestPolling from "../../../hooks/useReportRequestPolling";
import { getOperationTimezone } from "../../../utils/timezone";
import {
  INCIDENT_STATUS,
  INCIDENT_SEVERITY,
  INCIDENT_CATEGORIES,
} from "../../../constants/incidents";
import {
  BarChart3, Building2, Truck, Landmark, AlertTriangle, UserRound,
  Boxes, PackageCheck, ClipboardList, PackagePlus, Package,
  Calendar, Clock, Send, AlertCircle, CheckCircle2,
  FileSpreadsheet, FileText,
} from "lucide-react";

const REPORT_TYPES = [
  { value: "general", labelKey: "General", Icon: BarChart3, descriptionKey: "Todos los paquetes de tu empresa logística" },
  { value: "client", labelKey: "Por cliente", Icon: Building2, descriptionKey: "Paquetes de un cliente específico" },
  { value: "delivery", labelKey: "Por repartidor", Icon: Truck, descriptionKey: "Paquetes de un repartidor específico" },
  { value: "holding", labelKey: "Informe de Holding", Icon: Landmark, descriptionKey: "Paquetes de todas las empresas de un holding" },
  { value: "incidents", labelKey: "Incidencias", Icon: AlertTriangle, descriptionKey: "Incidencias registradas en el período" },
  { value: "driver_data", labelKey: "Datos de repartidores", Icon: UserRound, descriptionKey: "Snapshot con datos personales y bancarios de los repartidores activos" },
];

const GENERAL_CATEGORIES = [
  { value: "collects", labelKey: "Informe de Colectas", Icon: Boxes, descriptionKey: "Resumen y detalle de colectas del período" },
  { value: "deliveries", labelKey: "Informe de Entregas", Icon: PackageCheck, descriptionKey: "Paquetes entregados en el período" },
  { value: "all_packages", labelKey: "Todos los paquetes", Icon: ClipboardList, descriptionKey: "Todos los paquetes por fecha de recolección" },
  { value: "created", labelKey: "Creados", Icon: PackagePlus, descriptionKey: "Paquetes creados en el período, sin importar su estado actual" },
];

// Presets del wizard: el set canónico pero con "Este mes hasta hoy" en lugar de
// "Este mes" (mes completo), para no generar un endDate futuro al exportar.
const REPORT_PRESETS = DEFAULT_PRESETS.map((p) =>
  p.key === "thisMonth" ? { ...p, key: "thisMonthToToday" } : p,
);

const localDateToUtcRange = (dateStr, timezone) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  const end = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));

  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMs = getTimezoneOffsetMs(tz, start);
  return {
    start: new Date(start.getTime() + offsetMs),
    end: new Date(end.getTime() + offsetMs),
  };
};

const getTimezoneOffsetMs = (tz, refDate) => {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(refDate).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    const localTime = new Date(
      `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}:${parts.second}Z`
    );
    return refDate.getTime() - localTime.getTime();
  } catch {
    return 0;
  }
};

export default function CreateInforms() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id || null;
  const userTimezone = getOperationTimezone(user);

  const [selectedType, setSelectedType] = useState(null);
  const [generalCategory, setGeneralCategory] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [datePreset, setDatePreset] = useState("");
  const [clientList, setClientList] = useState([]);
  const [clientSelected, setClientSelected] = useState(null);
  const [deliveryList, setDeliveryList] = useState([]);
  const [deliverySelected, setDeliverySelected] = useState(null);
  const [holdingList, setHoldingList] = useState([]);
  const [holdingSelected, setHoldingSelected] = useState(null);
  const [incidentStatusFilter, setIncidentStatusFilter] = useState("");
  const [incidentSeverityFilter, setIncidentSeverityFilter] = useState("");
  const [incidentCategoryFilter, setIncidentCategoryFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const { requests, loadingList, refetch } = useReportRequestPolling();

  useEffect(() => {
    if (!logisticCompanyId) return;

    getActiveClientsApi(logisticCompanyId)
      .then((data) => {
        setClientList(
          (data || []).map((c) => ({ label: c?.name, value: c?._id }))
        );
      })
      .catch(console.error);

    getDeliveryUsers("active", "delivery", logisticCompanyId)
      .then((data) => {
        setDeliveryList(
          (data?.docs || []).map((u) => ({
            label: `${u?.name} ${u?.lastName}`,
            value: u?._id,
          }))
        );
      })
      .catch(console.error);

    getHoldings()
      .then((data) => {
        setHoldingList(
          (data || []).map((h) => ({
            label: h?.name,
            value: h?._id,
            companies: h?.companies,
          }))
        );
      })
      .catch(console.error);
  }, [logisticCompanyId]);

  const handleTypeSelect = (typeValue) => {
    setSelectedType(typeValue);
    setGeneralCategory(null);
    setClientSelected(null);
    setDeliverySelected(null);
    setHoldingSelected(null);
    setIncidentStatusFilter("");
    setIncidentSeverityFilter("");
    setIncidentCategoryFilter("");
    setSubmitError(null);
    setSuccessMsg(null);
  };

  const isSubmitDisabled = () => {
    if (!selectedType) return true;
    if (selectedType === "driver_data") return false;
    if (!startDate || !endDate) return true;
    if (selectedType === "general" && !generalCategory) return true;
    if (selectedType === "client" && !clientSelected) return true;
    if (selectedType === "delivery" && !deliverySelected) return true;
    if (selectedType === "holding" && !holdingSelected) return true;
    if (new Date(endDate) < new Date(startDate)) return true;
    return false;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSuccessMsg(null);

    const isSnapshotReport = selectedType === "driver_data";

    let payload;
    if (isSnapshotReport) {
      payload = { type: selectedType };
    } else {
      const startRange = localDateToUtcRange(startDate, userTimezone);
      const endRange = localDateToUtcRange(endDate, userTimezone);

      if (!startRange || !endRange) {
        setSubmitError(t("Fechas inválidas. Verifica el rango seleccionado."));
        setSubmitting(false);
        return;
      }

      payload = {
        type: selectedType,
        startDate: startRange.start.toISOString(),
        endDate: endRange.end.toISOString(),
        timezone: userTimezone,
      };
    }

    if (selectedType === "general") payload.generalCategory = generalCategory;
    if (selectedType === "client") {
      payload.clientId = clientSelected;
      payload.clientName = clientList.find((c) => c.value === clientSelected)?.label || "";
    }
    if (selectedType === "delivery") {
      payload.deliveryId = deliverySelected;
      payload.deliveryName = deliveryList.find((d) => d.value === deliverySelected)?.label || "";
    }
    if (selectedType === "holding") payload.holdingId = holdingSelected;
    if (selectedType === "incidents") {
      if (incidentStatusFilter) payload.incidentStatus = incidentStatusFilter;
      if (incidentSeverityFilter) payload.incidentSeverity = incidentSeverityFilter;
      if (incidentCategoryFilter) payload.incidentCategory = incidentCategoryFilter;
    }

    const result = await createReportRequest(payload);

    if (result?.error) {
      const msg =
        result.error?.message ||
        result.error?.error ||
        t("Error al crear el informe. Intente nuevamente.");
      setSubmitError(msg);
    } else {
      setSuccessMsg(t("Informe solicitado exitosamente. Aparecerá en el listado a continuación."));
      setSelectedType(null);
      setGeneralCategory(null);
      setStartDate("");
      setEndDate("");
      setDatePreset("");
      setClientSelected(null);
      setDeliverySelected(null);
      setHoldingSelected(null);
      setIncidentStatusFilter("");
      setIncidentSeverityFilter("");
      setIncidentCategoryFilter("");
      await refetch();
      setTimeout(() => setSuccessMsg(null), 6000);
    }

    setSubmitting(false);
  };

  const selectedHoldingData = holdingList.find((h) => h.value === holdingSelected);

  return (
    <div className="w-100 all-orders-container mt-6 ir-page">
      <div className="ir-header">
        <h2 className="ir-title">{t('Informes')}</h2>
        <p className="ir-subtitle">
          {t('Solicita informes personalizados de tus paquetes. Se procesan y podrás descargarlos cuando estén listos.')}
        </p>
      </div>

      <div className="ir-layout">
        <section className="ir-form-card" aria-label={t("Solicitar informe")}>
          <h3 className="ir-section-title">{t('Solicitar informe')}</h3>

          <div className="ir-step">
            <span className="ir-step-label">1. {t('Tipo de informe')}</span>
            <div className="ir-type-grid">
              {REPORT_TYPES
                .filter((rt) => rt.value !== "driver_data" || !!logisticCompanyId)
                .map((rt) => (
                <button
                  key={rt.value}
                  className={`ir-type-btn ${selectedType === rt.value ? "ir-type-btn--active" : ""}`}
                  onClick={() => handleTypeSelect(rt.value)}
                  type="button"
                >
                  <span className="ir-type-icon"><rt.Icon size={20} strokeWidth={1.75} /></span>
                  <span className="ir-type-name">{t(rt.labelKey)}</span>
                  <span className="ir-type-desc">{t(rt.descriptionKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {selectedType === "general" && (
            <div className="ir-step">
              <span className="ir-step-label">2. {t('Categoría del informe')}</span>
              <div className="ir-type-grid">
                {GENERAL_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    className={`ir-type-btn ${generalCategory === cat.value ? "ir-type-btn--active" : ""}`}
                    onClick={() => setGeneralCategory(cat.value)}
                    type="button"
                  >
                    <span className="ir-type-icon"><cat.Icon size={20} strokeWidth={1.75} /></span>
                    <span className="ir-type-name">{t(cat.labelKey)}</span>
                    <span className="ir-type-desc">{t(cat.descriptionKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedType !== "driver_data" && (
          <div className="ir-step">
            <span className="ir-step-label">{(selectedType === "general" || selectedType === "incidents") ? "3." : "2."} {t('Rango de fechas')}</span>
            <DateRangeFilter
              compact
              value={datePreset}
              presets={REPORT_PRESETS}
              onChange={(s, e, p) => {
                setStartDate(s);
                setEndDate(e);
                setDatePreset(p);
              }}
            />
            {startDate && endDate && new Date(endDate) < new Date(startDate) && (
              <p className="ir-validation-error">
                {t('La fecha de fin debe ser mayor o igual a la de inicio.')}
              </p>
            )}
          </div>
          )}

          {selectedType === "client" && (
            <div className="ir-step">
              <span className="ir-step-label">3. {t('Cliente')}</span>
              <SearchDropdown
                placeholder={t('Buscar y seleccionar cliente...')}
                options={clientList}
                onChange={setClientSelected}
                selected={clientList.find((c) => c.value === clientSelected)?.label}
                tooltip
              />
            </div>
          )}

          {selectedType === "delivery" && (
            <div className="ir-step">
              <span className="ir-step-label">3. {t('Repartidor')}</span>
              <SearchDropdown
                placeholder={t('Buscar y seleccionar repartidor...')}
                options={deliveryList}
                onChange={setDeliverySelected}
                selected={deliveryList.find((d) => d.value === deliverySelected)?.label}
                tooltip
              />
            </div>
          )}

          {selectedType === "holding" && (
            <div className="ir-step">
              <span className="ir-step-label">3. {t('Holding')}</span>
              <SearchDropdown
                placeholder={t('Buscar y seleccionar holding...')}
                options={holdingList}
                onChange={setHoldingSelected}
                selected={selectedHoldingData?.label}
                tooltip
              />
              {selectedHoldingData?.companies?.length > 0 && (
                <div className="ir-holding-companies">
                  <span className="ir-holding-companies-label">
                    {t('Empresas incluidas')} ({selectedHoldingData.companies.length})
                  </span>
                  <div className="ir-holding-tags">
                    {selectedHoldingData.companies.map((c) => (
                      <span key={c._id || c.value || String(c)} className="ir-holding-tag">
                        {c.name || t('Empresa')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedType === "incidents" && (
            <div className="ir-step">
              <span className="ir-step-label">2. {t('Filtros opcionales')}</span>
              <div className="ir-incident-filters">
                <div className="ir-filter-field">
                  <label className="ir-field-label" htmlFor="ir-incident-status">
                    {t('Estado')}
                  </label>
                  <select
                    id="ir-incident-status"
                    className="ir-date-input"
                    value={incidentStatusFilter}
                    onChange={(e) => setIncidentStatusFilter(e.target.value)}
                  >
                    <option value="">{t('Todos')}</option>
                    {Object.entries(INCIDENT_STATUS).map(([key, label]) => (
                      <option key={key} value={key}>{t(label)}</option>
                    ))}
                  </select>
                </div>
                <div className="ir-filter-field">
                  <label className="ir-field-label" htmlFor="ir-incident-severity">
                    {t('Criticidad')}
                  </label>
                  <select
                    id="ir-incident-severity"
                    className="ir-date-input"
                    value={incidentSeverityFilter}
                    onChange={(e) => setIncidentSeverityFilter(e.target.value)}
                  >
                    <option value="">{t('Todas')}</option>
                    {Object.entries(INCIDENT_SEVERITY).map(([key, label]) => (
                      <option key={key} value={key}>{t(label)}</option>
                    ))}
                  </select>
                </div>
                <div className="ir-filter-field">
                  <label className="ir-field-label" htmlFor="ir-incident-category">
                    {t('Categoría')}
                  </label>
                  <select
                    id="ir-incident-category"
                    className="ir-date-input"
                    value={incidentCategoryFilter}
                    onChange={(e) => setIncidentCategoryFilter(e.target.value)}
                  >
                    <option value="">{t('Todas')}</option>
                    {Object.entries(INCIDENT_CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>{t(label)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div className="ir-alert ir-alert--error" role="alert">
              <span className="ir-alert-icon"><AlertCircle size={18} /></span>
              <span>{submitError}</span>
            </div>
          )}

          {successMsg && (
            <div className="ir-alert ir-alert--success" role="alert">
              <span className="ir-alert-icon"><CheckCircle2 size={18} /></span>
              <span>{successMsg}</span>
            </div>
          )}

          <div className="ir-form-actions">
            <button
              className="ir-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitDisabled() || submitting}
              type="button"
            >
              {submitting ? (
                <>
                  <span className="ir-spinner" aria-hidden="true" />
                  {t('Enviando solicitud...')}
                </>
              ) : (
                <>
                  <Send size={16} aria-hidden="true" />
                  {t('Solicitar informe')}
                </>
              )}
            </button>
          </div>
        </section>

        <ReportRequestList
          requests={requests}
          loading={loadingList}
          onRefresh={refetch}
          resolveTypeLabel={(req) => {
            const typeData = REPORT_TYPES.find((rt) => rt.value === req.type);
            const generalCategoryData = req.type === "general" && req.generalCategory
              ? GENERAL_CATEGORIES.find((c) => c.value === req.generalCategory)
              : null;
            const IconComp = generalCategoryData?.Icon || typeData?.Icon || FileText;
            return {
              label: generalCategoryData
                ? `${typeData ? t(typeData.labelKey) : humanizeReportType(req.type)} — ${t(generalCategoryData.labelKey)}`
                : typeData ? t(typeData.labelKey) : humanizeReportType(req.type),
              icon: <IconComp size={15} aria-hidden="true" />,
            };
          }}
          renderMetadata={(req, { formatDateOnly, formatDisplayDate }) => (
            <>
              {req.startDateUtc && (
                <span className="ir-meta-item">
                  <Calendar size={13} aria-hidden="true" />
                  {formatDateOnly(req.startDateUtc, req.timezone)} —{" "}
                  {formatDateOnly(req.endDateUtc, req.timezone)}
                </span>
              )}
              <span className="ir-meta-item">
                <Clock size={13} aria-hidden="true" />
                {t('Solicitado')}: {formatDisplayDate(req.createdAt)}
              </span>
              {req.type === "delivery" && req.filters?.deliveryName && (
                <span className="ir-meta-item">{t('Repartidor')}: {req.filters.deliveryName}</span>
              )}
              {req.type === "client" && req.filters?.clientName && (
                <span className="ir-meta-item">{t('Cliente')}: {req.filters.clientName}</span>
              )}
            </>
          )}
          renderRowCount={(req) => {
            if (req.rowCount == null) return null;
            const RowIcon = req.type === "incidents" ? AlertTriangle : req.type === "driver_data" ? UserRound : Package;
            const unit = req.type === "incidents" ? t("incidencias") : req.type === "driver_data" ? t("repartidores") : t("paquetes");
            return <span className="ir-meta-item ir-meta-rows"><RowIcon size={13} aria-hidden="true" /> {req.rowCount} {unit}</span>;
          }}
          renderDownloadButtons={(req, handleDownload) => (
            <>
              {req.files?.xlsxUrl && (
                <button className="ir-download-btn ir-download-btn--xlsx" onClick={() => handleDownload(req.files.xlsxUrl, `informe_${req._id}.xlsx`)} type="button" title={t('Descargar en formato Excel')}>
                  <FileSpreadsheet size={14} aria-hidden="true" /> Excel
                </button>
              )}
              {req.files?.csvUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvUrl, `informe_${req._id}.csv`)} type="button" title={t('Descargar en formato CSV')}>
                  <FileText size={14} aria-hidden="true" /> CSV
                </button>
              )}
              {req.files?.csvCollectSummaryUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvCollectSummaryUrl, `colectas_resumen_${req._id}.csv`)} type="button" title={t('CSV de Colectas (Resumen)')}>
                  <FileText size={14} aria-hidden="true" /> CSV {t('Colectas')}
                </button>
              )}
              {req.files?.csvCollectDetailedUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvCollectDetailedUrl, `colectas_detalle_${req._id}.csv`)} type="button" title={t('CSV de Colectas (Detalle)')}>
                  <FileText size={14} aria-hidden="true" /> CSV {t('Colectas')} {t('Detalle')}
                </button>
              )}
              {req.files?.csvDeliveryUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvDeliveryUrl, `entregas_${req._id}.csv`)} type="button" title={t('CSV de Entregas')}>
                  <FileText size={14} aria-hidden="true" /> CSV {t('Entregas')}
                </button>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
}
